// Core
import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { motion, useAnimation } from 'framer-motion';
import { Box, Image, Divider, Button, useColorMode, useColorModeValue } from '@chakra-ui/react';
import { NavLink, Link } from 'react-router-dom';

// Assets
import { apiService } from 'services/ApiService';
import placeholderPartner from 'assets/img/placeholder-partner.jpg';

const PartnersDoubles = () => {
    const borderRight = useColorModeValue('1px solid #CBE7FF', '1px solid rgba(255, 255, 255, 0.10)');
    const bgDivider = 'linear-gradient(90deg, rgba(56, 169, 225, 0.00) 9.63%, #38A9E1 37.41%, #9EDFFF 50%, #38A9E1 60.02%, rgba(56, 169, 225, 0.00) 91.14%)';

    const { colorMode } = useColorMode();
    const isDark = colorMode === 'dark';

    const { data, isLoading } = useQuery({
        queryKey: ['data'],
        queryFn: async () => {
            const response = await apiService.getPartners({ limit: 50, offset: 0 });
            return response.data;
        },
        refetchOnWindowFocus: false
    });

    const list = Array.isArray(data) ? data : [];

    const listToLeft = useAnimation();
    const listToRight = useAnimation();

    useEffect(() => {
        if (!data) { return; }

        const animateLeft = async () => {
            await listToLeft.start({
                x: ['0%', '-100%'], 
                transition: { duration: 50, ease: 'linear', repeat: Infinity, repeatType: 'reverse' },
            });
        };

        animateLeft();

        return () => listToLeft.stop();
    }, [data, listToLeft]);

    useEffect(() => {
        if (!data) { return; } 

        const animateRight = async () => {
            await listToRight.start({
                x: ['-100%', '0%'],
                transition: { duration: 50, ease: 'linear', repeat: Infinity, repeatType: 'reverse' },
            });
        };

        animateRight();

        return () => listToRight.stop();
    }, [data, listToRight]);

    return (
        <>
            {isLoading ? ( 
                <Box textAlign="center" mt="50px">Loading...</Box>
            ) : (
                <>
                    <Box zIndex="1" overflow="visible" width="100vw" mt="32px" whiteSpace="nowrap" position="relative">
                        <motion.div
                            style={{ display: 'flex', gap: '0px' }}
                            animate={listToLeft}
                        >
                            {[...list, ...list].map((item, index) => (
                                <Box 
                                    key={`top-${index}`} 
                                    p="0px" 
                                    borderRight={borderRight} 
                                    overflow="hidden"
                                    minW={{base: '200px', md: '264px'}}
                                    maxH={{base: '115px', md: '175px'}}
                                    background="#0d1633"
                                    w="100%" 
                                    textAlign="center">
                                    <Image src={item.image ?? placeholderPartner} alt={item.name} w="100%" />
                                </Box>
                            ))}
                        </motion.div>
                    </Box>

                    <Box zIndex="2" position="relative" w="100%">
                        <Box bg={bgDivider} h="1px" />
                        <Box bg="#099BFF" filter="blur(38px)" w="100%" h="16px" mt="-10px" position="absolute" />
                        <Box bg="white" filter="blur(8px)" borderRadius="100%" opacity="1" w="50%" h="4px" ml="30%" mt="-6px" position="absolute" />
                        { isDark && <Box bg="#099BFF" filter="blur(60px)" opacity="0.8" w="150px" h="300px" top="-150px" left="50%" ml="-75px" position="absolute" />}

                        <NavLink to="/discounts">
                            <Button 
                                left="50%"
                                ml="-75px"
                                mt="-29px"
                                position="absolute"
                                variant={ isDark ? 'button-lg-g' : 'button-lg'}
                                w="150px">See All Discounts</Button>
                        </NavLink>
                    </Box>

                    <Box zIndex="1" overflow="visible" width="100vw" whiteSpace="nowrap" position="relative">
                        <motion.div
                            style={{ display: 'flex', gap: '0px' }}
                            animate={listToRight}
                            initial={{ x: '-100%' }}
                        >
                            {[...list, ...list].map((item, index) => (
                                <Box 
                                    key={`bottom-${index}`} 
                                    p="0px" 
                                    borderRight={borderRight} 
                                    overflow="hidden"
                                    minW={{base: '200px', md: '264px'}}
                                    maxH={{base: '115px', md: '175px'}}
                                    background="#0d1633"
                                    w="100%" 
                                    textAlign="center">
                                    <Image src={item.image ?? placeholderPartner} alt={item.name} w="100%" />
                                </Box>
                            ))}
                        </motion.div>
                    </Box>
                </>
            )}
        </>
    );
};

export default PartnersDoubles;